import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { AutoColumn } from '../../components/Column'
import { TYPE } from '../../theme'
import {
  asBillionsKishu,
  burnSupplyAsKishu,
  fetchKishuStats,
  formatPercent,
  formatTokenAmount,
  formatUsd,
  KishuStatsPayload
} from '../../utils/kishuStats'

const DEAD_ADDRESS = '0x000000000000000000000000000000000000dEaD'
const KISHU_TOKEN_ADDRESS = '0xA2b4C0Af19cC16a6CfAcCe81F192B024d625817D'
const KISHU_BURN_LINK = `https://etherscan.io/token/${KISHU_TOKEN_ADDRESS}?a=${DEAD_ADDRESS}`

const PageWrapper = styled(AutoColumn)`
  max-width: 760px;
  width: 100%;
`

const BurnedBody = styled.div`
  width: 100%;
  padding: 24px;
  background: ${({ theme }) => theme.bg1};
  box-shadow: 0 24px 32px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.08);
  border-radius: 30px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    align-items: flex-start;
    flex-direction: column;
  `};
`

const RefreshButton = styled.button`
  border: 0;
  border-radius: 18px;
  padding: 10px 18px;
  color: white;
  background: radial-gradient(174.47% 188.91% at 1.84% 0%, #0cccfc 0%, #021d43 100%);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  :disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const HeroNumber = styled.div`
  color: ${({ theme }) => theme.text1};
  font-size: 34px;
  font-weight: 700;
  line-height: 1.1;
  margin: 10px 0 2px;
  overflow-wrap: anywhere;
`

const SubNumber = styled.div`
  color: ${({ theme }) => theme.text2};
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 22px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    grid-template-columns: 1fr;
  `};
`

const StatCell = styled.div`
  min-width: 0;
  padding: 14px 16px;
  border-radius: 18px;
  background: ${({ theme }) => theme.bg2};
`

const Label = styled.div`
  color: ${({ theme }) => theme.text2};
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
`

const Value = styled.div`
  color: ${({ theme }) => theme.text1};
  font-size: 18px;
  font-weight: 600;
  overflow-wrap: anywhere;
`

const BurnLink = styled.a`
  color: ${({ theme }) => theme.primary1};
  font-size: 18px;
  font-weight: 600;
  text-decoration: none;

  :hover {
    text-decoration: underline;
  }
`

const StatusText = styled.div`
  color: ${({ theme }) => theme.text2};
  font-size: 14px;
`

const ErrorText = styled(StatusText)`
  color: ${({ theme }) => theme.red1};
`

function burnedRows(stats: KishuStatsPayload) {
  const burnedKishu = burnSupplyAsKishu(stats)
  const burnPercent = stats.maxSupply ? (burnedKishu / stats.maxSupply) * 100 : 0
  const burnedUsd = burnedKishu * stats.kishuPriceUsdt

  return [
    { label: 'Burned (Billions KISHU)', value: `${formatTokenAmount(asBillionsKishu(burnedKishu), 2)} B` },
    { label: 'Percent of Max Supply Burned', value: formatPercent(burnPercent, 4) },
    { label: 'Estimated Burn Value', value: formatUsd(burnedUsd, 2) },
    { label: '1 Billion KISHU Price', value: formatUsd(stats.kishuPriceUsdt * 1000000000, 4) },
    { label: 'Max Supply', value: `${formatTokenAmount(stats.maxSupply, 0)} KISHU` },
    { label: 'Last Updated', value: stats.timestamp }
  ]
}

export default function Burned() {
  const [stats, setStats] = useState<KishuStatsPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadStats = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      setStats(await fetchKishuStats())
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Burned stats request failed')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  const burnedKishu = stats ? burnSupplyAsKishu(stats) : 0

  return (
    <PageWrapper>
      <BurnedBody>
        <Toolbar>
          <AutoColumn gap="4px">
            <TYPE.mediumHeader>Burned</TYPE.mediumHeader>
            <StatusText>{loading ? 'Loading burn data...' : 'KISHU sent to the burn address'}</StatusText>
          </AutoColumn>
          <RefreshButton onClick={loadStats} disabled={loading}>
            Refresh
          </RefreshButton>
        </Toolbar>

        {error && <ErrorText>{error}</ErrorText>}

        {stats && (
          <>
            <Label>Total Burned</Label>
            <HeroNumber>{formatTokenAmount(burnedKishu, 2)} KISHU</HeroNumber>
            <SubNumber>{formatTokenAmount(asBillionsKishu(burnedKishu), 2)} billion KISHU</SubNumber>

            <Grid>
              {burnedRows(stats).map(row => (
                <StatCell key={row.label}>
                  <Label>{row.label}</Label>
                  <Value>{row.value}</Value>
                </StatCell>
              ))}
              <StatCell>
                <Label>Burn Address</Label>
                <BurnLink href={KISHU_BURN_LINK} target="_blank" rel="noopener noreferrer">
                  View on Etherscan
                </BurnLink>
              </StatCell>
            </Grid>
          </>
        )}
      </BurnedBody>
    </PageWrapper>
  )
}
